import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  PhoneIcon,
  UserGroupIcon,
  ChatBubbleBottomCenterTextIcon,
  PhoneArrowUpRightIcon,
  PhoneArrowDownLeftIcon,
  PhoneXMarkIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { dashboardAPI, cdrAPI } from '../../services/api';
import { useAuthStore } from '../../stores/authStore';

const DashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [recentCalls, setRecentCalls] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useAuthStore();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsResponse, callsResponse] = await Promise.all([
          dashboardAPI.getStats(),
          cdrAPI.getAll({ limit: 5 }),
        ]);
        
        setStats(statsResponse.data);
        setRecentCalls(callsResponse.data?.records || callsResponse.data || []);
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Dashboard</h1>
        <p className="text-slate-400 mt-1">
          Welcome back, {user?.name}. Here's an overview of your phone system.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Extensions"
          value={stats?.extensions?.total || 0}
          icon={PhoneIcon}
          href="/extensions"
          color="primary"
        />
        <StatCard
          title="Ring Groups"
          value={stats?.ringGroups?.total || 0}
          icon={UserGroupIcon}
          href="/ring-groups"
          color="accent"
        />
        <StatCard
          title="IVR Menus"
          value={stats?.ivr?.total || 0}
          icon={ChatBubbleBottomCenterTextIcon}
          href="/ivr"
          color="purple"
        />
        <StatCard
          title="Calls Today"
          value={stats?.calls?.total || 0}
          icon={PhoneArrowUpRightIcon}
          href="/call-history"
          color="green"
        />
      </div>

      {/* Call Statistics */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <CallStatCard
          title="Answered Calls"
          value={stats?.calls?.answered || 0}
          icon={PhoneArrowDownLeftIcon}
          color="green"
        />
        <CallStatCard
          title="Missed Calls"
          value={stats?.calls?.missed || 0}
          icon={PhoneXMarkIcon}
          color="red"
        />
        <CallStatCard
          title="Avg. Duration"
          value={formatDuration(stats?.calls?.avgDuration || 0)}
          icon={ClockIcon}
          color="blue"
        />
      </div>

      {/* Quick Actions & Recent Calls */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Quick Actions */}
        <div className="card">
          <h2 className="text-lg font-semibold text-white mb-4">Quick Actions</h2>
          <div className="grid grid-cols-2 gap-3">
            <QuickActionButton
              to="/extensions/new"
              icon={PhoneIcon}
              label="Add Extension"
            />
            <QuickActionButton
              to="/ring-groups/new"
              icon={UserGroupIcon}
              label="Create Ring Group"
            />
            <QuickActionButton
              to="/ivr/new"
              icon={ChatBubbleBottomCenterTextIcon}
              label="Create IVR"
            />
            <QuickActionButton
              to="/call-history"
              icon={ClockIcon}
              label="View Call History"
            />
          </div>
        </div>

        {/* Recent Calls */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">Recent Calls</h2>
            <Link
              to="/call-history"
              className="text-sm text-primary-400 hover:text-primary-300"
            >
              View all
            </Link>
          </div>
          
          {recentCalls.length > 0 ? (
            <div className="space-y-3">
              {recentCalls.slice(0, 5).map((call, index) => (
                <RecentCallItem key={call.id || index} call={call} />
              ))}
            </div>
          ) : (
            <p className="text-slate-500 text-center py-8">No recent calls</p>
          )}
        </div>
      </div>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ title, value, icon: Icon, href, color }) => {
  const colorClasses = {
    primary: 'from-primary-500/20 to-primary-600/10 border-primary-500/30',
    accent: 'from-accent-500/20 to-accent-600/10 border-accent-500/30',
    purple: 'from-purple-500/20 to-purple-600/10 border-purple-500/30',
    green: 'from-green-500/20 to-green-600/10 border-green-500/30',
  };

  const iconColors = {
    primary: 'text-primary-400',
    accent: 'text-accent-400',
    purple: 'text-purple-400',
    green: 'text-green-400',
  };

  return (
    <Link
      to={href}
      className={`stat-card bg-gradient-to-br ${colorClasses[color]} group`}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-slate-400">{title}</p>
          <p className="text-3xl font-bold text-white mt-1">{value}</p>
        </div>
        <div className={`p-3 rounded-lg bg-slate-800/50 ${iconColors[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
      <div className="mt-4 flex items-center text-sm text-slate-500 group-hover:text-primary-400 transition-colors">
        View details
        <svg className="w-4 h-4 ml-1 transform group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </Link>
  );
};

// Call Stat Card
const CallStatCard = ({ title, value, icon: Icon, color }) => {
  const colorClasses = {
    green: 'text-green-400 bg-green-500/20',
    red: 'text-red-400 bg-red-500/20',
    blue: 'text-blue-400 bg-blue-500/20',
  };

  return (
    <div className="card flex items-center gap-4">
      <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
        <Icon className="w-6 h-6" />
      </div>
      <div>
        <p className="text-sm text-slate-400">{title}</p>
        <p className="text-2xl font-bold text-white">{value}</p>
      </div>
    </div>
  );
};

// Quick Action Button
const QuickActionButton = ({ to, icon: Icon, label }) => (
  <Link
    to={to}
    className="flex items-center gap-3 p-3 rounded-lg bg-slate-800/50 border border-slate-700/50 hover:border-primary-500/30 hover:bg-slate-800 transition-all duration-200 group"
  >
    <div className="p-2 rounded-lg bg-slate-700/50 text-slate-400 group-hover:text-primary-400 transition-colors">
      <Icon className="w-5 h-5" />
    </div>
    <span className="text-sm font-medium text-slate-300 group-hover:text-white transition-colors">
      {label}
    </span>
  </Link>
);

// Recent Call Item
const RecentCallItem = ({ call }) => {
  const isInbound = call.direction === 'inbound' || call.type === 'inbound';
  const isAnswered = call.disposition === 'ANSWERED' || call.status === 'answered';

  return (
    <div className="flex items-center justify-between p-3 rounded-lg bg-slate-800/30 hover:bg-slate-800/50 transition-colors">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${isInbound ? 'bg-blue-500/20 text-blue-400' : 'bg-green-500/20 text-green-400'}`}>
          {isInbound ? (
            <PhoneArrowDownLeftIcon className="w-4 h-4" />
          ) : (
            <PhoneArrowUpRightIcon className="w-4 h-4" />
          )}
        </div>
        <div>
          <p className="text-sm font-medium text-white">
            {call.src || call.caller_id || 'Unknown'}
          </p>
          <p className="text-xs text-slate-500">
            {call.dst || call.destination || 'Unknown'} • {formatCallTime(call.calldate || call.timestamp)}
          </p>
        </div>
      </div>
      <span className={`badge ${isAnswered ? 'badge-success' : 'badge-danger'}`}>
        {isAnswered ? 'Answered' : 'Missed'}
      </span>
    </div>
  );
};

// Helper functions
const formatDuration = (seconds) => {
  if (!seconds || seconds === 0) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const formatCallTime = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

export default DashboardPage;
